import { GoogleAnalyticsEscriptEventName } from '@/hooks/analytics/types';
import useGoogleAnalytics from '@/hooks/analytics/useGoogleAnalytics';
import { hashedItem } from '@/hooks/analytics/utils';
import useCustomer from '@/hooks/user/useCustomer';
import { validateEscriptToken } from '@montugroup/circuit-shared/utils/escript';
import { emitUIEvent } from '@montugroup/data-collection';
import { Box, Button, IconButton, Typography } from '@montugroup/design-system';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import { Grid2 } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import type { SubmitErrorHandler } from 'react-hook-form';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { TokenInputField } from './TokenInputField';
import { TokenTextField } from './TokenTextField';
import type { AddNewScriptFormFields, AddNewScriptFormTokenUnion, AddNewScriptProps } from './types';

const TokenContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(6),
}));

const TokenFieldContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(4),
  marginBottom: theme.spacing(6),
}));

const TokenFieldWrapper = styled(Box)({
  flex: 1,
});

const IconButtonContainer = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
});

export function AddNewScript({ handleTokenSubmit }: AddNewScriptProps) {
  const [additionalFields, setAdditionalFields] = useState<number[]>([]);
  const [openTokenDialog, setOpenTokenDialog] = useState(false);
  const { sendGoogleAnalyticsEvent } = useGoogleAnalytics();
  const { data: customer } = useCustomer();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useFormContext<AddNewScriptFormTokenUnion>();

  // Watch all form fields to enable/disable submit button
  const formValues = useWatch({
    control,
  });

  const handleAddField = () => {
    setAdditionalFields([...additionalFields, additionalFields.length + 1]);
  };

  const handleRemoveField = (indexToRemove: number) => {
    setAdditionalFields(additionalFields.filter((idx) => idx !== indexToRemove));
  };

  const isAnyFieldFilled = () => {
    // Check if the main token input is filled
    if (formValues?.tokenInput?.trim()) {
      return true;
    }

    // Check additional fields
    return additionalFields.some((index) => {
      const key = `additionalToken${index}` as keyof AddNewScriptFormFields;
      const fieldValue = (formValues as AddNewScriptFormFields)?.[key]?.trim();
      return Boolean(fieldValue);
    });
  };

  useEffect(() => {
    if (errors.tokenInput) {
      // GA event to track validation failures
      sendGoogleAnalyticsEvent(GoogleAnalyticsEscriptEventName.PRESCRIPTION_SUBMIT_ERROR, {
        error_code: errors.tokenInput.message,
        circuit_identifier: customer?.code ? hashedItem(customer.code) : '',
      });

      // snowplow event to track validation failures
      emitUIEvent(
        {
          component_name: 'add_new_script',
          event_name: 'error',
          id: 'escript_onboarding_add_new_script_error',
          value: errors.tokenInput.message,
        },
        [],
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors.tokenInput]);

  const onSubmit = (data: AddNewScriptFormTokenUnion) => {
    // If there are no additional fields, submit as SubmitTokenData
    if (additionalFields.length === 0) {
      if (data.tokenInput) {
        handleTokenSubmit({ tokenInput: data.tokenInput });
      }
      return;
    }

    // If there are additional fields, submit as AddNewScriptFormFields
    const filteredData: AddNewScriptFormFields = { tokenInput: '' };

    if (data.tokenInput) {
      filteredData.tokenInput = data.tokenInput;
    }

    additionalFields.forEach((index) => {
      const key = `additionalToken${index}` as keyof AddNewScriptFormFields;
      const value = (data as AddNewScriptFormFields)[key];
      if (value) {
        filteredData[key] = value;
      }
    });

    // Only submit if at least one field has a value
    if (filteredData.tokenInput || Object.keys(filteredData).length > 1) {
      handleTokenSubmit(filteredData);
    }
  };

  const onTokenInvalid: SubmitErrorHandler<AddNewScriptFormTokenUnion> = () => {
    if (errors.tokenInput) {
      // snowplow event to track validation failures
      emitUIEvent(
        {
          component_name: 'add_new_script',
          event_name: 'error',
          id: 'escript_add_new_script_error',
          value: errors.tokenInput.message,
        },
        [],
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onTokenInvalid)} data-testid="add-new-script">
      <Grid2 container spacing={6}>
        <Grid2 size={{ xs: 12 }}>
          <Typography fontWeight="bold" variant="h5">
            Add new script
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 8 }}>
          <Typography variant="body2">
            Enter your script tokens here. We will assess whether your medication is available.
          </Typography>
          <TokenContainer>
            {/* Initial TokenInputField with Add button */}
            <TokenFieldContainer>
              <IconButtonContainer>
                <IconButton aria-label="add token" size="large" onClick={handleAddField}>
                  <Add />
                </IconButton>
              </IconButtonContainer>

              <TokenFieldWrapper>
                <Controller
                  name="tokenInput"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'Please enter your script token',
                    validate: validateEscriptToken,
                  }}
                  render={({ field, fieldState }) => (
                    <TokenInputField
                      field={field}
                      fieldState={fieldState}
                      formErrors={errors}
                      openTokenDialog={openTokenDialog}
                      setOpenTokenDialog={setOpenTokenDialog}
                    />
                  )}
                />
              </TokenFieldWrapper>
            </TokenFieldContainer>

            {/* Additional TokenTextFields with delete buttons */}
            {additionalFields.map((fieldIndex) => (
              <TokenFieldContainer key={fieldIndex}>
                <IconButtonContainer>
                  <IconButton aria-label="delete token" size="large" onClick={() => handleRemoveField(fieldIndex)}>
                    <Delete />
                  </IconButton>
                </IconButtonContainer>

                <TokenFieldWrapper>
                  <Controller
                    name={`additionalToken${fieldIndex}`}
                    control={control}
                    defaultValue=""
                    rules={{
                      required: 'Please enter your script token',
                      validate: validateEscriptToken,
                    }}
                    render={({ field, fieldState }) => (
                      <TokenTextField<AddNewScriptFormTokenUnion, `additionalToken${number}`>
                        field={field}
                        fieldState={fieldState}
                      />
                    )}
                  />
                </TokenFieldWrapper>
              </TokenFieldContainer>
            ))}
          </TokenContainer>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!isAnyFieldFilled()}
            size="large"
            sx={(theme) => ({
              [theme.breakpoints.down('xs')]: {
                width: '100%',
                position: 'fixed',
              },

              width: '100%',
            })}
          >
            Submit
          </Button>
        </Grid2>
      </Grid2>
    </form>
  );
}
